import 'swiper/css';
import 'swiper/css/pagination';

import { Chip } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useId } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import Link from '@/components/Link/Link';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import StarRating, {
  type StarRatingValidFilledValues,
} from '@/components/StarRatingComponent/StarRatingComponent';
import { type Contentful, ContentStyle, type LinkContent } from '@/contentful';
import useGlobalStyles from '@/styles/global.styles';

import useStyles from './HighlightTestimonial.styles';

export interface HighlightTestimonialCardContent extends Contentful {
  fields: {
    testimonial: string;
    quoteAttribution: string;
    attributionLink?: string;
    productChips?: string[];
    rating: boolean;
    starRating: StarRatingValidFilledValues;
  };
}

export interface HighlightTestimonialContent extends Contentful {
  fields: {
    style?: ContentStyle;
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    cta?: LinkContent;
    testimonialCards: HighlightTestimonialCardContent[];
  };
}

interface HighlightTestimonialProps {
  content: HighlightTestimonialContent;
}

export default function HighlightTestimonial({
  content: {
    fields: { style, eyebrow, heading, subheading, cta, testimonialCards },
  },
}: HighlightTestimonialProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const isOnDark = style === ContentStyle.OnDark;
  const { classes, cx } = useStyles({ isOnDark });
  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');

  return (
    <div className={cx(classes.container, style)}>
      <GridContainer2 fixed>
        <Grid2 xs={12} md={6}>
          <SectionHeader
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
            isOnDark={isOnDark}
            fixed
          />
        </Grid2>
        {cta && (
          <Grid2 xs={12} md={6} className={classes.cta}>
            <Button
              isOnDark={isOnDark}
              size={{ xs: ButtonSize.SM, sm: ButtonSize.MD }}
              label={cta.fields.text}
              href={cta.fields.url}
              variant={ButtonVariant.SECONDARY}
              className={classes.button}
            />
          </Grid2>
        )}
      </GridContainer2>
      <div className={classes.testimonialsContainer}>
        <Swiper
          navigation={{
            prevEl: `#${prevElId}`,
            nextEl: `#${nextElId}`,
            disabledClass: classes.navigationButtonDisabled,
          }}
          modules={[Navigation, Pagination]}
          slidesPerView={1}
          pagination={{
            el: '#bullet-pagination',
            bulletActiveClass: 'bullet-active',
            bulletClass: 'bullet',
            horizontalClass: classes.horizontal,
            clickable: true,
            // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
            renderBullet: (index: number, className: string) => {
              return `<svg class="${className}" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill="currentColor"/>
              </svg>`;
            },
          }}
        >
          {testimonialCards.map(
            (
              {
                fields: {
                  testimonial,
                  quoteAttribution,
                  attributionLink,
                  rating,
                  starRating,
                  productChips,
                },
              },
              index
            ) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
              <SwiperSlide key={index} className={classes.swiperSlide}>
                <GridContainer2
                  fixed
                  gridProps={{ className: classes.testimonialGridContainer }}
                >
                  <Grid2 xs={12}>
                    <div className={classes.testimonial}>
                      <div className={classes.starQuoteContainer}>
                        <p
                          className={cx(
                            globalClasses.body,
                            classes.quoteAttribution
                          )}
                        >
                          {attributionLink ? (
                            <Link href={attributionLink}>
                              {quoteAttribution}
                            </Link>
                          ) : (
                            quoteAttribution
                          )}
                        </p>
                        {rating && (
                          <StarRating
                            value={starRating}
                            isOnDark={isOnDark}
                            starHeight={34}
                          />
                        )}
                      </div>

                      <h3
                        className={classes.testimonialText}
                      >{`"${testimonial}"`}</h3>
                      {productChips && (
                        <div className={classes.chips}>
                          {productChips?.map((productChip, index) => (
                            <Chip
                              // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                              key={index}
                              label={productChip}
                              className={classes.chip}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </Grid2>
                </GridContainer2>
              </SwiperSlide>
            )
          )}
        </Swiper>
        <div
          className={cx(
            classes.navigation,
            testimonialCards.length < 2 && classes.hidden
          )}
        >
          <div id={prevElId} className={classes.navigationButton}>
            <Icon name={IconName.ARROW_LEFT} width={24} />
          </div>
          {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
          <div id='bullet-pagination' className={classes.pagination}></div>
          <div id={nextElId} className={classes.navigationButton}>
            <Icon name={IconName.ARROW_RIGHT} width={24} />
          </div>
        </div>
      </div>
    </div>
  );
}
